// packages/shared/src/schemas/inventory.ts
import { z } from 'zod';

// ============================================
// ENUMS
// ============================================

export const InventoryCategoryEnum = z.enum([
  'GAMING_PC',
  'GAMING_CONSOLE',
  'VR_EQUIPMENT',
  'FURNITURE',
  'SPORTS_EQUIPMENT',
  'AUDIO_EQUIPMENT',
  'OTHER',
]);
export type InventoryCategoryValue = z.infer<typeof InventoryCategoryEnum>;

export const InventoryRequestStatusEnum = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type InventoryRequestStatusValue = z.infer<typeof InventoryRequestStatusEnum>;

// ============================================
// INVENTORY ITEM SCHEMAS (Admin)
// ============================================

export const CreateInventoryItemSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  description: z.string().max(1000).optional(),
  category: InventoryCategoryEnum,
  specifications: z.record(z.string(), z.unknown()).optional(),
  icon: z.string().max(100).optional(),
  displayOrder: z.number().int().optional(),
});
export type CreateInventoryItemRequest = z.infer<typeof CreateInventoryItemSchema>;

export const UpdateInventoryItemSchema = CreateInventoryItemSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateInventoryItemRequest = z.infer<typeof UpdateInventoryItemSchema>;

export const InventoryItemFilterSchema = z.object({
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
  category: InventoryCategoryEnum.optional(),
  search: z.string().optional(),
  isActive: z.coerce.boolean().optional(),
});
export type InventoryItemFilterQuery = z.infer<typeof InventoryItemFilterSchema>;

// ============================================
// ADDON SCHEMAS (Admin)
// ============================================

export const CreateInventoryAddonSchema = z.object({
  addonItemId: z.string().uuid('Invalid addon item ID'),
  includedQuantity: z.number().int().min(0).default(0),
  maxAdditionalQuantity: z.number().int().min(1).optional(),
  defaultPricePerHour: z.number().positive().optional(),
  defaultPricePerHalfHour: z.number().positive().optional(),
  isRequired: z.boolean().default(false),
  displayOrder: z.number().int().optional(),
});
export type CreateInventoryAddonRequest = z.infer<typeof CreateInventoryAddonSchema>;

export const UpdateInventoryAddonSchema = CreateInventoryAddonSchema.partial();
export type UpdateInventoryAddonRequest = z.infer<typeof UpdateInventoryAddonSchema>;

// ============================================
// VENDOR REQUEST SCHEMAS
// ============================================

export const CreateInventoryRequestSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100),
  description: z.string().max(1000).optional(),
  category: InventoryCategoryEnum,
  specifications: z.record(z.string(), z.any()).optional(),
  suggestedImage: z.string().url().optional(),
});
export type CreateInventoryRequestPayload = z.infer<typeof CreateInventoryRequestSchema>;

export const ReviewInventoryRequestSchema = z
  .object({
    approve: z.boolean(),
    reviewNotes: z.string().optional(),
  })
  .refine((data) => data.approve === true || (data.approve === false && data.reviewNotes), {
    message: 'Review notes required when rejecting',
    path: ['reviewNotes'],
  });
export type ReviewInventoryRequestPayload = z.infer<typeof ReviewInventoryRequestSchema>;

// ============================================
// VENUE INVENTORY SCHEMAS (Vendor)
// ============================================

export const AddVenueInventorySchema = z.object({
  inventoryItemId: z.string().uuid('Invalid inventory item ID'),
  quantity: z.number().int().min(1).default(1),
  pricePerHour: z.number().positive().optional(),
  pricePerHalfHour: z.number().positive().optional(),
  maxPlayers: z.number().int().min(1).optional(),
  condition: z.string().max(100).optional(),
  notes: z.string().max(500).optional(),
});
export type AddVenueInventoryRequest = z.infer<typeof AddVenueInventorySchema>;

export const UpdateVenueInventorySchema = z.object({
  quantity: z.number().int().min(0).optional(),
  pricePerHour: z.number().positive().optional().nullable(),
  pricePerHalfHour: z.number().positive().optional().nullable(),
  maxPlayers: z.number().int().min(1).optional().nullable(),
  condition: z.string().max(100).optional(),
  notes: z.string().max(500).optional(),
  isAvailable: z.boolean().optional(),
});
export type UpdateVenueInventoryRequest = z.infer<typeof UpdateVenueInventorySchema>;

export const SetVenueAddonPricingSchema = z.object({
  addonItemId: z.string().uuid('Invalid addon item ID'),
  pricePerHour: z.number().positive().optional().nullable(),
  pricePerHalfHour: z.number().positive().optional().nullable(),
  maxQuantity: z.number().int().min(1).optional().nullable(),
  isAvailable: z.boolean().default(true),
});
export type SetVenueAddonPricingRequest = z.infer<typeof SetVenueAddonPricingSchema>;

// ============================================
// RESPONSE TYPES
// ============================================

export interface InventoryItemResponse {
  id: string;
  name: string;
  slug: string;
  description?: string;
  category: InventoryCategoryValue;
  specifications?: Record<string, unknown>;
  icon?: string;
  isActive: boolean;
  displayOrder: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  addons?: InventoryAddonResponse[];
}

export interface InventoryAddonResponse {
  id: string;
  addonItemId: string;
  addonItem: {
    id: string;
    name: string;
    slug: string;
    category: InventoryCategoryValue;
  };
  includedQuantity: number;
  maxAdditionalQuantity?: number;
  defaultPricePerHour?: number;
  defaultPricePerHalfHour?: number;
  isRequired: boolean;
  displayOrder: number;
}

export interface InventoryRequestResponse {
  id: string;
  vendorId: string;
  vendor?: { id: string; businessName: string };
  name: string;
  description?: string;
  category: InventoryCategoryValue;
  specifications?: Record<string, unknown>;
  suggestedImage?: string;
  status: InventoryRequestStatusValue;
  reviewedBy?: string;
  reviewedAt?: string;
  reviewNotes?: string;
  createdItemId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface VenueInventoryResponse {
  id: string;
  venueId: string;
  inventoryItemId: string;
  inventoryItem: InventoryItemResponse;
  quantity: number;
  pricePerHour?: number;
  pricePerHalfHour?: number;
  maxPlayers?: number;
  condition?: string;
  notes?: string;
  isAvailable: boolean;
  addonPricing?: VenueAddonPricingResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface VenueAddonPricingResponse {
  id: string;
  addonItemId: string;
  pricePerHour?: number;
  pricePerHalfHour?: number;
  maxQuantity?: number;
  isAvailable: boolean;
}

// ========================
// Table Management (Phase 1)
// ========================

export const AvailabilityModeSchema = z.enum(['ALL', 'WALKIN_ONLY']);

export const InventoryUnitSchema = z.object({
  id: z.string().uuid(),
  venueInventoryId: z.string().uuid(),
  label: z.string().min(1).max(100),
  displayOrder: z.number().int().default(999),
  isActive: z.boolean().default(true),
  availabilityMode: AvailabilityModeSchema.default('ALL'),
  notes: z.string().max(500).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const UpdateInventoryUnitSchema = z.object({
  label: z.string().min(1).max(100).optional(),
  availabilityMode: AvailabilityModeSchema.optional(),
  isActive: z.boolean().optional(),
  notes: z.string().max(500).nullable().optional(),
  displayOrder: z.number().int().optional(),
});

// Canonical definition — import this in bookings.ts, do NOT redefine there
export const AssignUnitsSchema = z.object({
  assignments: z
    .array(
      z.object({
        venueInventoryId: z.string().uuid(),
        inventoryUnitId: z.string().uuid(),
      })
    )
    .min(1),
});

export const ReassignUnitSchema = z.object({
  bookingInventoryId: z.string().uuid(),
  fromUnitId: z.string().uuid(),
  toUnitId: z.string().uuid(),
});

export type AssignUnitsRequest = z.infer<typeof AssignUnitsSchema>;
export type ReassignUnitRequest = z.infer<typeof ReassignUnitSchema>;
